<template lang="pug">
    .scheduled-reports-section
        .section-header(@click="expanded = !expanded")
            .header-content
                v-icon.mr-2(:class="{ 'rotate-icon': expanded }") mdi-chevron-right
                span.header-title Scheduled Reports
                v-chip.ml-2(small :color="scheduledReports.length > 0 ? 'primary' : 'grey'") {{ scheduledReports.length }}
            v-btn(
                v-if="expanded"
                icon
                small
                @click.stop="$emit('refresh')"
                :loading="loading"
            )
                v-icon(small) mdi-refresh
        
        v-expand-transition
            .section-content(v-show="expanded")
                v-progress-linear(v-if="loading" indeterminate color="primary")
                
                .no-schedules(v-if="!loading && scheduledReports.length === 0")
                    v-icon.mb-2(size="48" color="grey") mdi-calendar-clock
                    p No scheduled reports yet
                    small Select devices and measurements, choose a preset time period, then click "Schedule Report".
                
                v-simple-table.haq-data-table(v-if="!loading && scheduledReports.length > 0")
                    template(v-slot:default)
                        thead
                            tr
                                th Name
                                th Time Period
                                th Frequency
                                th Send Time
                                th Format
                                th Contents
                                th Recipients
                                th Status
                                th Last Run
                                th
                                    | Actions
                                    v-tooltip(bottom max-width="280")
                                        template(v-slot:activator="{ on, attrs }")
                                            v-btn(icon small aria-label="About previews" v-bind="attrs" v-on="on")
                                                v-icon(small) mdi-help-circle-outline
                                        span Preview downloads the saved report. No email is sent.
                        tbody
                            tr(v-for="schedule in scheduledReports" :key="schedule.id")
                                td.name-cell {{ schedule.name }}
                                td
                                    v-chip(small outlined) {{ getTimelineLabel(schedule.timeline) }}
                                td
                                    v-chip(small outlined) {{ getFrequencyLabel(schedule.frequency) }}
                                td.send-time-cell
                                    span {{ formatSendTime(schedule.sendTime) }}
                                    small.timezone-hint(v-if="schedule.timezone") {{ getTimezoneAbbr(schedule.timezone) }}
                                td
                                    v-chip(
                                        v-for="format in schedule.formats"
                                        :key="format"
                                        small
                                        class="mr-1"
                                        :color="format === 'xlsx' || format === 'csv' ? 'green' : 'blue'"
                                        text-color="white"
                                    ) {{ formatLabel(format) }}
                                td
                                    div(v-if="schedule.formats.some(f => f === 'xlsx' || f === 'csv')") Excel: {{ schedule.includeTrendData !== false ? 'Summary + readings' : 'Summary only' }}
                                    div(v-if="schedule.formats.includes('pdf')") PDF: {{ schedule.includeTrendData !== false ? 'Summary + readings' : 'Summary only' }}{{ schedule.includeCharts ? ' + charts' : '' }}
                                td
                                    v-tooltip(bottom)
                                        template(v-slot:activator="{ on, attrs }")
                                            v-chip(small v-bind="attrs" v-on="on") {{ schedule.recipients.length }} user{{ schedule.recipients.length !== 1 ? 's' : '' }}
                                        span Recipients will receive emails {{ getFrequencyLabel(schedule.frequency).toLowerCase() }}
                                td
                                    v-chip(
                                        small
                                        :color="schedule.isActive ? 'success' : 'grey'"
                                        :text-color="schedule.isActive ? 'white' : 'white'"
                                    )
                                        v-icon(left x-small) {{ schedule.isActive ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                                        | {{ schedule.isActive ? 'Active' : 'Paused' }}
                                td
                                    span(v-if="schedule.lastRun") {{ formatDate(schedule.lastRun) }}
                                    span.never-run(v-else) Never
                                td.actions-cell
                                    v-btn.haq-btn.haq-btn-secondary(
                                        v-for="format in [...new Set(schedule.formats.map(f => f === 'csv' ? 'xlsx' : f))]"
                                        :key="format"
                                        :loading="previewingId === schedule.id + ':' + format"
                                        :disabled="!!previewingId"
                                        @click="$emit('preview', { schedule, format })"
                                    ) Preview {{ formatLabel(format) }}
                                    v-btn(icon small @click="$emit('edit', schedule)")
                                        v-icon(small) mdi-pencil
                                    v-btn(icon small color="error" @click="confirmDelete(schedule)")
                                        v-icon(small) mdi-delete
        
        //- Delete Confirmation Dialog
        v-dialog(v-model="deleteDialog" content-class="haq-dialog" max-width="400")
            v-card.haq-dialog-card
                v-card-title Delete Scheduled Report
                v-card-text
                    | Are you sure you want to delete the scheduled report "{{ deletingSchedule?.name }}"?
                    br
                    | This action cannot be undone.
                v-card-actions
                    v-spacer
                    v-btn.haq-btn.haq-btn-secondary(@click="deleteDialog = false") Cancel
                    v-btn.haq-btn.haq-btn-primary(@click="confirmDeleteAction") Delete
</template>

<script>
import moment from 'moment-timezone';

export default {
    name: 'ScheduledReportsTable',
    props: {
        previewingId: { type: String, default: '' },
        siteId: {
            type: String,
            required: true
        },
        scheduledReports: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            expanded: false,
            deleteDialog: false,
            deletingSchedule: null
        };
    },
    computed: {
        timelineLabels() {
            return {
                yesterday: 'Yesterday',
                lastWeek: 'Last Week',
                lastMonth: 'Last Month'
            };
        },
        frequencyLabels() {
            return {
                daily: 'Daily',
                weekly: 'Weekly',
                monthly: 'Monthly'
            };
        },
        timezoneAbbreviations() {
            return {
                'America/New_York': 'ET',
                'America/Chicago': 'CT',
                'America/Denver': 'MT',
                'America/Los_Angeles': 'PT',
                'America/Anchorage': 'AKT',
                'Pacific/Honolulu': 'HT'
            };
        }
    },
    watch: {
        // Auto-expand if there are scheduled reports
        scheduledReports: {
            immediate: true,
            handler(reports) {
                if (reports && reports.length > 0) {
                    this.expanded = true;
                }
            }
        }
    },
    methods: {
        getTimelineLabel(timeline) {
            return this.timelineLabels[timeline] || timeline;
        },
        getFrequencyLabel(frequency) {
            return this.frequencyLabels[frequency] || frequency || 'Daily';
        },
        /**
         * Chip label for a stored format value.
         * @description Shows Excel for both xlsx and legacy csv values.
         * @param {string} format
         * @returns {string}
         */
        formatLabel(format) {
            if (format === 'csv' || format === 'xlsx') return 'EXCEL';
            return (format || '').toUpperCase();
        },
        /**
         * Convert 24h "HH:00" to 12h display like "6:00 AM"
         */
        formatSendTime(sendTime) {
            if (!sendTime) return '6:00 AM';
            const hour = parseInt(sendTime.split(':')[0], 10);
            if (hour === 0) return '12:00 AM';
            if (hour < 12) return hour + ':00 AM';
            if (hour === 12) return '12:00 PM';
            return (hour - 12) + ':00 PM';
        },
        getTimezoneAbbr(tz) {
            return this.timezoneAbbreviations[tz] || tz || '';
        },
        formatDate(dateString) {
            if (!dateString) return 'Never';
            return moment(dateString).format('MMM D, YYYY h:mm A');
        },
        confirmDelete(schedule) {
            this.deletingSchedule = schedule;
            this.deleteDialog = true;
        },
        confirmDeleteAction() {
            if (this.deletingSchedule) {
                this.$emit('delete', this.deletingSchedule);
            }
            this.deleteDialog = false;
            this.deletingSchedule = null;
        }
    }
};
</script>

<style lang="scss" scoped>
.scheduled-reports-section {
    margin-top: 32px;
    background-color: var(--app-secondary-bg);
    border-radius: 12px;
    border: 1px solid var(--border);
    overflow: hidden;
    
    .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 16px 20px;
        cursor: pointer;
        transition: background-color 0.2s;
        
        &:hover {
            background-color: var(--app-tertiary-bg);
        }
        
        .header-content {
            display: flex;
            align-items: center;
        }
        
        .header-title {
            color: var(--app-primary-txt);
            font-size: 16px;
            font-weight: 500;
        }
        
        .rotate-icon {
            transform: rotate(90deg);
            transition: transform 0.2s;
        }
        
        .v-icon:not(.rotate-icon) {
            transition: transform 0.2s;
        }
    }
    
    .section-content {
        border-top: 1px solid var(--border);
        
        .no-schedules {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
            color: var(--app-tertiary-txt);
            
            p {
                margin-bottom: 4px;
                color: var(--app-primary-txt);
            }
            
            small {
                text-align: center;
                max-width: 400px;
            }
        }
        
        .preview-help {
            margin: 0;
            padding: 16px 24px 8px;
            color: var(--app-secondary-txt);
            font-size: 14px;
            line-height: 1.5;
        }

        .haq-data-table {
            background-color: transparent !important;
            
            th {
                color: var(--app-tertiary-txt) !important;
                font-weight: 600;
                white-space: nowrap;
            }
            
            td {
                color: var(--app-primary-txt);
            }
            
            .name-cell {
                font-weight: 500;
            }
            
            .never-run {
                color: var(--app-tertiary-txt);
                font-style: italic;
            }
            
            .send-time-cell {
                white-space: nowrap;
                
                .timezone-hint {
                    display: inline-block;
                    margin-left: 4px;
                    color: var(--app-tertiary-txt);
                    font-size: 11px;
                }
            }
            
            .actions-cell {
                white-space: nowrap;
                .v-btn.haq-btn {
                    height: 36px !important;
                    min-width: 0 !important;
                    padding: 0 12px !important;
                    margin-right: 6px;
                    letter-spacing: 0.3px !important;
                    text-transform: none !important;
                }
            }
        }
    }
}

// Dialog styling
.v-dialog {
    .v-card {
        background-color: var(--app-secondary-bg) !important;
        
        .v-card__title {
            color: var(--app-primary-txt);
        }
        
        .v-card__text {
            color: var(--app-secondary-txt);
        }
    }
}
</style>
